/*
  Sticky "Add to cart" bar for the product page (markup in snippets/sticky-atc.liquid).

  Slides in under the sticky header as soon as the visitor starts scrolling
  down, and hides again when they're back at the top. The bar's button
  just clicks the real one, so the selected variant, quantity, sold-out state
  and cart drawer all behave exactly as they do in the main form.

  The Shop Pay / Buy it now button can't be proxied that way, so it sits in a
  small form of its own whose variant id and quantity are copied from the
  main form.
*/
if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.button = this.querySelector('.sticky-atc__button');
        this.productInfo = document.getElementById('MainProduct-' + this.sectionId);
        this.checkoutForm = this.querySelector('.sticky-atc__checkout');

        // product-info.js fires change on the main form's id input after a
        // variant switch; quantity-input fires change on the quantity field.
        this.onFormChange = (event) => {
          if (event.target.closest && event.target.closest('#product-form-' + this.sectionId + ', #Quantity-Form-' + this.sectionId)) {
            this.syncCheckoutForm();
          }
        };
        if (this.checkoutForm) document.addEventListener('change', this.onFormChange);

        this.button.addEventListener('click', () => {
          const main = this.mainButton();
          if (main && !main.disabled) main.click();
        });

        if (!this.productInfo) return;

        // Show as soon as the visitor starts scrolling down, hide again back
        // at the top. While visible, keep tracking the header's bottom edge:
        // anything above the sticky header (announcement bar) scrolls away
        // during the first few hundred pixels. Batched to one update per frame.
        this.onScroll = () => {
          cancelAnimationFrame(this.scrollFrame);
          this.scrollFrame = requestAnimationFrame(() => {
            this.toggle(window.scrollY > 0);
            if (this.visible) this.positionBelowHeader();
          });
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();

        // Variant changes rewrite the main button's label and disabled state;
        // mirror them so the bar never offers an unavailable variant. Batched
        // to one sync per frame however many mutations arrive.
        this.mutationObserver = new MutationObserver(() => {
          cancelAnimationFrame(this.syncFrame);
          this.syncFrame = requestAnimationFrame(() => this.syncButton());
        });
        this.mutationObserver.observe(this.productInfo, {
          subtree: true,
          childList: true,
          attributes: true,
          attributeFilter: ['disabled'],
        });

        this.onResize = () => this.visible && this.positionBelowHeader();
        window.addEventListener('resize', this.onResize, { passive: true });
      }

      disconnectedCallback() {
        if (this.mutationObserver) this.mutationObserver.disconnect();
        cancelAnimationFrame(this.syncFrame);
        cancelAnimationFrame(this.scrollFrame);
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onResize);
        document.removeEventListener('change', this.onFormChange);
      }

      // Looked up each time: variant changes can replace the button element.
      mainButton() {
        return document.getElementById('ProductSubmitButton-' + this.sectionId);
      }

      syncButton() {
        const main = this.mainButton();
        if (!main) return;
        const label = main.querySelector('span');
        const text = (label ? label.textContent : main.textContent).trim();
        if (text && this.button.textContent.trim() !== text) this.button.textContent = text;
        this.button.disabled = main.disabled;
      }

      syncCheckoutForm() {
        if (!this.checkoutForm) return;
        const copy = (name, source) => {
          const target = this.checkoutForm.querySelector('input[name="' + name + '"]');
          if (!source || !target || !source.value || target.value === source.value) return;
          target.value = source.value;
          target.dispatchEvent(new Event('change', { bubbles: true }));
        };
        const mainForm = document.getElementById('product-form-' + this.sectionId);
        copy('id', mainForm && mainForm.querySelector('input[name="id"]'));
        copy('quantity', document.getElementById('Quantity-' + this.sectionId));
      }

      positionBelowHeader() {
        const header = document.querySelector('.section-header');
        const bottom = header ? Math.max(header.getBoundingClientRect().bottom, 0) : 0;
        this.style.setProperty('--sticky-atc-top', bottom + 'px');
      }

      toggle(show) {
        if (show === this.visible) return;
        this.visible = show;
        if (show) {
          this.positionBelowHeader();
          this.syncButton();
          this.syncCheckoutForm();
        }
        this.classList.toggle('is-visible', show);
        this.setAttribute('aria-hidden', String(!show));
        this.button.tabIndex = show ? 0 : -1;
      }
    }
  );
}
